import { useMemo } from 'react';

interface ParsedKey {
  baseKey: string;
  imageState?: string;
  displayKey: string;
  keyType: 'spell' | 'summoner' | 'auto' | 'item' | 'rightclick' | 'leftclick';
  target?: string;
  description?: string;
  requiresConfirmation?: boolean;
  subSequenceId?: number;
  subSequencePosition?: number;
}

interface ComboData {
  id: string;
  name: string;
  description: string;
  championName: string;
  difficulty: string;
  category: string;
  type: 'simple-combo' | 'jungle-clear';
  inputSequence: string;
  detailedSequence: any[];
}

interface KitingSettings {
  enabled: boolean;
  rightClickKey: string;
  hideRCButtons: boolean;
  hideLCButtons: boolean;
  attackMoveClick: boolean;
}

interface UserInputKey {
  key: string;
  isCorrect: boolean;
  isOptional?: boolean;
}

interface SequenceState {
  displaySequence: ParsedKey[];
  inputSequence: string[];
  sequenceChunks: ParsedKey[][];
  currentProgress: {
    currentKeyIndex: number;
    currentChunkIndex: number;
    completedKeys: number;
    totalKeys: number;
    errorCount: number;
  };
  keyStates: Array<{
    key: ParsedKey;
    status: 'completed' | 'current' | 'upcoming';
    userInput?: UserInputKey;
  }>;
}

export const useSequenceLogic = (
  selectedCombo: ComboData | null,
  customSequence: string,
  kitingSettings: KitingSettings,
  userInputKeys: UserInputKey[],
  currentKeyIndex: number,
  parseSequence: (sequence: string) => ParsedKey[],
  parseInputSequence: (sequence: string) => string[],
  convertDetailedKey: (detailedKey: any, index: number) => ParsedKey,
  currentSubSequence: number,
  subSequencePosition: number
): SequenceState => {
  
  return useMemo(() => {
    // Reduced console logging - check the visual debug component for detailed sequence info

    // Get base sequence
    let baseSequence: ParsedKey[] = [];
    let baseInputSequence: string[] = [];
    
    if (selectedCombo) {
      baseSequence = selectedCombo.detailedSequence.map((detailedKey, index) => 
        convertDetailedKey(detailedKey, index)
      );
      baseInputSequence = parseInputSequence(selectedCombo.inputSequence);
    } else if (customSequence) {
      baseSequence = parseSequence(customSequence);
      baseInputSequence = parseInputSequence(customSequence);
    }
    
    // Apply kiting modifications
    let displaySequence = baseSequence;
    let inputSequence = baseInputSequence;
    
    if (kitingSettings.enabled && baseSequence.length > 0) {
      
      // Create kited display sequence with sub-sequences
      const kitedDisplaySequence: ParsedKey[] = [];
      let subSequenceIndex = 0;
      
      baseSequence.forEach((key, index) => {
        // Add the main key
        kitedDisplaySequence.push({
          ...key,
          subSequenceId: subSequenceIndex,
          subSequencePosition: 0
        });
        
        // Add left-click after auto attacks if attack-move click is enabled
        if (key.keyType === 'auto' && kitingSettings.attackMoveClick) {
          const lcKey: ParsedKey = {
            baseKey: 'LC',
            displayKey: 'LC',
            keyType: 'leftclick',
            description: 'Left Click to confirm auto attack',
            subSequenceId: subSequenceIndex,
            subSequencePosition: 1
          };
          kitedDisplaySequence.push(lcKey);
        }
        
        // Add right-click after every sub-sequence except the last one (for display only)
        const isLastKey = index === baseSequence.length - 1;
        if (!isLastKey) {
          const rcKey: ParsedKey = {
            baseKey: 'RC',
            displayKey: 'RC',
            keyType: 'rightclick',
            description: 'Right Click to kite',
            subSequenceId: -1, // RC doesn't belong to any sub-sequence
            subSequencePosition: -1
          };
          kitedDisplaySequence.push(rcKey);
        }
        
        // Increment sub-sequence index for next main key
        subSequenceIndex++;
      });
      
      // For input sequence, NEVER add right-clicks - they're always optional
      // Only add required left-clicks for attack-move
      const kitedInputSequence: string[] = [];
      baseInputSequence.forEach((key) => {
        kitedInputSequence.push(key);
        
        // Add left-click after auto attacks if attack-move click is enabled
        // This is still required even when kiting is enabled
        if (key === 'A' && kitingSettings.attackMoveClick) {
          kitedInputSequence.push('LC');
        }
      });
      
      // Apply visibility filters to display sequence
      let filteredDisplaySequence = kitedDisplaySequence;
      if (kitingSettings.hideRCButtons) {
        filteredDisplaySequence = filteredDisplaySequence.filter(key => key.keyType !== 'rightclick');
      }
      if (kitingSettings.hideLCButtons) {
        filteredDisplaySequence = filteredDisplaySequence.filter(key => key.keyType !== 'leftclick');
      }
      
      displaySequence = filteredDisplaySequence;
      inputSequence = kitedInputSequence; // Clean sequence without RC
    }
    
    // Create sequence chunks based on visual element counting
    // D, Q, etc. = 1 visual element, A+LC = 2 visual elements
    // Target: 6-7 visual elements per line, never split A+LC pairs
    const sequenceChunks: ParsedKey[][] = [];
    const minVisualElements = 6;
    const maxVisualElements = 7;
    
    let currentChunk: ParsedKey[] = [];
    let currentVisualCount = 0;
    
    for (let i = 0; i < displaySequence.length; i++) {
      const key = displaySequence[i];
      
      // Calculate visual elements this key represents
      let visualElements = 1;
      
      // Check if this is part of an A+LC sub-sequence (2 visual elements)
      if (key.subSequenceId !== undefined) {
        const subSequenceKeys = displaySequence.filter(k => k.subSequenceId === key.subSequenceId);
        if (subSequenceKeys.length > 1) {
          // This is a multi-key sub-sequence (like A+LC)
          visualElements = subSequenceKeys.length;
          // But we only count it once for the first key in the sub-sequence
          if (key.subSequencePosition !== 0) {
            visualElements = 0; // Don't count subsequent keys in the same sub-sequence
          }
        }
      }
      
      // Check if adding this key would exceed our target
      const wouldExceedMax = currentVisualCount + visualElements > maxVisualElements;
      const hasMinimum = currentVisualCount >= minVisualElements;
      
      // Check if this key starts a new sub-sequence
      const startsNewSubSequence = currentChunk.length > 0 && 
                                  key.subSequenceId !== undefined && 
                                  currentChunk[currentChunk.length - 1].subSequenceId !== key.subSequenceId;
      
      // If adding this key would exceed max AND we have minimum, start new chunk
      if (wouldExceedMax && hasMinimum) {
        // End current chunk before adding this key
        sequenceChunks.push([...currentChunk]);
        currentChunk = [];
        currentVisualCount = 0;
      }
      // Or if we're at a good sub-sequence boundary with reasonable size
      else if (startsNewSubSequence && hasMinimum) {
        // End current chunk before adding this key
        sequenceChunks.push([...currentChunk]);
        currentChunk = [];
        currentVisualCount = 0;
      }
      
      // Add key to current chunk
      currentChunk.push(key);
      currentVisualCount += visualElements;
      
      // If this is the last key, end the chunk
      if (i === displaySequence.length - 1) {
        sequenceChunks.push([...currentChunk]);
        currentChunk = [];
        currentVisualCount = 0;
      }
    }
    
    // Debug logging for chunking
    console.log('📦 CHUNKING RESULT:', sequenceChunks.map((chunk, index) => {
      const visualCount = chunk.reduce((count, key) => {
        if (key.subSequenceId !== undefined) {
          const subSeqKeys = displaySequence.filter(k => k.subSequenceId === key.subSequenceId);
          if (subSeqKeys.length > 1 && key.subSequencePosition === 0) {
            return count + subSeqKeys.length;
          } else if (subSeqKeys.length === 1) {
            return count + 1;
          }
          return count; // Don't count subsequent keys in sub-sequence
        }
        return count + 1;
      }, 0);
      return {
        chunkIndex: index,
        keyCount: chunk.length,
        visualCount,
        keys: chunk.map(k => k.baseKey).join('-')
      };
    }));
    
    // Calculate current key index from sub-sequence progress
    let actualCurrentKeyIndex = 0;
    for (const key of displaySequence) {
      if (key.subSequenceId === undefined) {
        // Handle keys without sub-sequence (like RC) - these are usually optional
        // For now, we'll advance past them if we're beyond their position
        if (actualCurrentKeyIndex < displaySequence.length - 1) {
          actualCurrentKeyIndex++;
        }
        continue;
      }
      
      if (key.subSequenceId < currentSubSequence) {
        // This entire sub-sequence is completed
        actualCurrentKeyIndex++;
      } else if (key.subSequenceId === currentSubSequence) {
        // We're currently in this sub-sequence
        if ((key.subSequencePosition || 0) < subSequencePosition) {
          // This position within the sub-sequence is completed
          actualCurrentKeyIndex++;
        } else if ((key.subSequencePosition || 0) === subSequencePosition) {
          // This is the current key position
          break;
        } else {
          // This position is upcoming within the current sub-sequence
          break;
        }
      } else {
        // This sub-sequence is upcoming
        break;
      }
    }
    
    actualCurrentKeyIndex = Math.min(actualCurrentKeyIndex, displaySequence.length);
    
    // Debug logging for progress tracking (can be removed later)
    // console.log('🎯 PROGRESS CALCULATION:', {
    //   currentSubSequence,
    //   subSequencePosition,
    //   actualCurrentKeyIndex,
    //   displaySequenceLength: displaySequence.length
    // });
    
    // Find which chunk contains the current key
    let currentChunkIndex = 0;
    let keysSoFar = 0;
    for (let i = 0; i < sequenceChunks.length; i++) {
      if (actualCurrentKeyIndex < keysSoFar + sequenceChunks[i].length) {
        currentChunkIndex = i;
        break;
      }
      keysSoFar += sequenceChunks[i].length;
    }
    
    const errorCount = userInputKeys.filter(key => !key.isCorrect && !key.isOptional).length;
    
    // Create a mapping of expected sequence positions to user inputs
    // This handles the case where optional inputs (right-clicks) don't advance the sequence
    let expectedKeyIndex = 0;
    const keyToUserInputMap: Map<number, UserInputKey> = new Map();
    
    for (let i = 0; i < userInputKeys.length && expectedKeyIndex < displaySequence.length; i++) {
      const userInput = userInputKeys[i];
      
      if (userInput.isOptional) {
        // Optional input (right-click in flexible mode) - don't map to expected sequence
        continue;
      }
      
      // Map this user input to the current expected key position
      keyToUserInputMap.set(expectedKeyIndex, userInput);
      
      // Only advance expected key index if this was a required input
      expectedKeyIndex++;
    }
    
    // Create key states with proper user input matching
    const keyStates = displaySequence.map((key, index) => {
      let status: 'completed' | 'current' | 'upcoming';
      if (index < actualCurrentKeyIndex) {
        status = 'completed';
      } else if (index === actualCurrentKeyIndex) {
        status = 'current';
      } else {
        status = 'upcoming';
      }
      
      // Get the mapped user input for this expected key position
      const userInput = keyToUserInputMap.get(index);
      
      return {
        key,
        status,
        userInput
      };
    });
    
    // Sequence state calculation complete - check visual debug component for details

    return {
      displaySequence,
      inputSequence,
      sequenceChunks,
      currentProgress: {
        currentKeyIndex: actualCurrentKeyIndex,
        currentChunkIndex,
        completedKeys: actualCurrentKeyIndex,
        totalKeys: displaySequence.length,
        errorCount
      },
      keyStates
    };
  }, [
    selectedCombo,
    customSequence,
    kitingSettings,
    userInputKeys,
    currentKeyIndex,
    parseSequence,
    parseInputSequence,
    convertDetailedKey,
    currentSubSequence,
    subSequencePosition
  ]);
};

export default useSequenceLogic;